import React from 'react';
import { calc, calendlyBaseUrl, fmtChf, fmtChfTight, fmtNum, generateConfigCode, hasMeaningfulInput } from './calc.js';
import { currentLocale, t } from './i18n.js';
const { useState, useEffect, useRef } = React;

// =============================================================================
// Shared atoms (bsv2)
// =============================================================================
const PulseDot = () => <span className="bsv2__live-dot" aria-hidden="true"></span>;

const ProgressDots = ({ stepIdx, total = 4 }) => (
  <div className="bsv2__progress" aria-hidden="true">
    {Array.from({ length: total }).map((_, i) => (
      <span key={i} className={`bsv2__progress-dot${i < stepIdx ? ' is-done' : ''}${i === stepIdx ? ' is-active' : ''}`}></span>
    ))}
  </div>
);

// Briefing teaser: synthetic CHF 80-240k range with a subtle live-feel rotation.
const useTeaserRange = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(v => v + 1), 1800);
    return () => clearInterval(id);
  }, []);
  const phase = tick % 4;
  return {
    lo: ['80', '85', '90', '85'][phase],
    hi: ['240', '260', '255', '245'][phase],
  };
};

// =============================================================================
// LiveMiniBar (mobile bottom bar — kept from previous sidebar)
// =============================================================================
function useBodySideOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const el = document.querySelector('.body__side');
    if (el) el.classList.toggle('is-open', open);
    return () => { if (el) el.classList.remove('is-open'); };
  }, [open]);
  return [open, setOpen];
}

const LiveMiniBar = ({ r, hasInput, open, onToggle, isResult }) => (
  <button
    type="button"
    className={`live__minibar${open ? ' is-open' : ''}`}
    onClick={onToggle}
    aria-expanded={open}
    aria-controls="live-sheet"
    aria-label={open ? t('live.minibar_close') : t('live.minibar_open')}
  >
    <div className="live__minibar-left">
      <span className="live__minibar-label">{isResult ? t('live.minibar_label_next') : t('live.minibar_label_live')}</span>
      <span className="live__minibar-value">
        {hasInput ? fmtChf(r.recommendedBudget) : '—'}
      </span>
    </div>
    <div className="live__minibar-right">
      <span className="live__minibar-status">{isResult ? t('live.minibar_status_ready') : t('live.updated')}</span>
      <i className={`fa-solid fa-chevron-${open ? 'down' : 'up'}`}></i>
    </div>
  </button>
);

// =============================================================================
// SidebarBriefing — Step 0
// =============================================================================
const CodeBar = ({ onLoadCode }) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const submit = () => {
    const v = code.trim().toUpperCase();
    if (!v || !onLoadCode) return;
    onLoadCode(v);
    setCode('');
    setOpen(false);
  };
  return (
    <div className={`bsv2__codebar${open ? ' is-open' : ''}`}>
      {!open ? (
        <button type="button" className="bsv2__codebar-toggle" onClick={() => setOpen(true)}>
          <i className="fa-solid fa-key"></i>
          <span>{t('briefing.code_toggle')}</span>
          <i className="fa-solid fa-chevron-right" style={{ marginLeft: 'auto', opacity: 0.5 }}></i>
        </button>
      ) : (
        <div className="bsv2__codebar-open">
          <label className="bsv2__codebar-label">
            <i className="fa-solid fa-key"></i> {t('briefing.code_label')}
          </label>
          <div className="bsv2__codebar-row">
            <input
              className="input"
              placeholder={t('live.load_placeholder')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              autoComplete="off"
              spellCheck={false}
              style={{ textTransform: 'uppercase', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, letterSpacing: '0.04em' }}
            />
            <button type="button" className="btn" data-size="sm" data-kind="primary" disabled={!code.trim()} onClick={submit}>
              {t('live.load_button')}
            </button>
            <button type="button" className="bsv2__codebar-close" onClick={() => { setOpen(false); setCode(''); }} aria-label={t('common.close')}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SidebarBriefing = ({ onLoadCode }) => {
  const { lo, hi } = useTeaserRange();
  const stats = [
    { icon: 'clock',         iconKind: 'regular', key: 'time' },
    { icon: 'shield-halved',                       key: 'local' },
    { icon: 'handshake',     iconKind: 'regular', key: 'private' },
    { icon: 'briefcase',                           key: 'projects' },
  ];
  return (
    <aside className="bsv2">
      <div className="bsv2__hero">
        <div className="bsv2__eyebrow">
          <PulseDot/>
          {t('preStep.section_label')}
        </div>
        <h2 className="bsv2__hero-title" dangerouslySetInnerHTML={{ __html: t('briefing.hero_title_html') }}/>
        <p className="bsv2__hero-sub">{t('briefing.hero_sub')}</p>
      </div>

      <div className="bsv2__teaser">
        <div className="bsv2__teaser-head">
          <span><i className="fa-solid fa-wand-magic-sparkles"></i> {t('briefing.teaser_head')}</span>
          <span className="bsv2__teaser-pending">{t('briefing.teaser_pending')}</span>
        </div>
        <div className="bsv2__teaser-big">
          <span className="bsv2__chf">CHF</span>
          <span className="bsv2__range">{lo}–{hi}<small>k</small></span>
        </div>
        <div className="bsv2__teaser-cap">{t('briefing.teaser_cap')}</div>
        <div className="bsv2__band">
          <div className="bsv2__band-track">
            <div className="bsv2__band-fill" style={{ left: '18%', right: '14%' }}></div>
            <div className="bsv2__band-pin" style={{ left: '54%' }}></div>
          </div>
          <div className="bsv2__band-labels">
            <span>{t('briefing.teaser_low')}</span>
            <span>{t('briefing.teaser_high')}</span>
          </div>
        </div>
        <ul className="bsv2__teaser-drivers">
          <li>{t('briefing.teaser_driver_1')}</li>
          <li>{t('briefing.teaser_driver_2')}</li>
          <li>{t('briefing.teaser_driver_3')}</li>
        </ul>
        <div className="bsv2__teaser-veil">
          <i className="fa-regular fa-eye-slash"></i>
          <span>{t('briefing.teaser_veil')}</span>
        </div>
      </div>

      <div className="bsv2__stats">
        {stats.map(s => (
          <div key={s.key} className="bsv2__stat">
            <i className={`fa-${s.iconKind || 'solid'} fa-${s.icon}`}></i>
            <div>
              <strong>{t(`briefing.stats.${s.key}.label`)}</strong>
              <span>{t(`briefing.stats.${s.key}.sub`)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="bsv2__note bsv2__note--noavatar">
        <div className="bsv2__note-body">
          <p>{t('briefing.note_body')}</p>
          <span className="bsv2__note-name">{t('briefing.note_name')}</span>
        </div>
      </div>

      <CodeBar onLoadCode={onLoadCode}/>

      <LegalLinks/>
    </aside>
  );
};

const LegalLinks = () => {
  const isEn = (typeof currentLocale === 'function') && currentLocale() === 'en';
  const base = isEn ? 'https://cloudtec.com/legal' : 'https://cloudtec.ch/info';
  const links = isEn
    ? [
        { label: 'Privacy',       path: 'datenschutz' },
        { label: 'Terms of use',  path: 'terms-of-use' },
        { label: 'AGB',           path: 'conditions' },
        { label: 'Imprint',       path: 'imprint' },
      ]
    : [
        { label: 'Datenschutz',           path: 'datenschutz' },
        { label: 'Nutzungsbedingungen',   path: 'nutzungsbedingungen' },
        { label: 'AGB',                   path: 'agb' },
        { label: 'Impressum',             path: 'impressum' },
      ];
  return (
    <nav className="bsv2__legal" aria-label={isEn ? 'Legal' : 'Rechtliches'}>
      {links.map((l, i) => (
        <React.Fragment key={l.path}>
          {i > 0 && <span className="bsv2__legal-sep" aria-hidden="true">·</span>}
          <a href={`${base}/${l.path}`} target="_blank" rel="noopener noreferrer">{l.label}</a>
        </React.Fragment>
      ))}
    </nav>
  );
};

// =============================================================================
// SidebarLive — Steps 1-3
// =============================================================================
const SidebarLive = ({ r, hasInput, step }) => {
  const stepIdx = step === 'p1' ? 1 : step === 'p2' ? 2 : step === 'p3' ? 3 : 0;
  const stepName = t(`stepper.${stepIdx}`);
  const totalDrivers = (r.allDrivers || []).filter(d => d.weight > 0).length;
  const drivers = (r.drivers || []).slice(0, 3);
  const tshirt = r.tShirtSize?.label || 'S';

  return (
    <aside className="bsv2">
      <div className="bsv2__hero bsv2__hero--compact">
        <div className="bsv2__eyebrow bsv2__eyebrow--row">
          <span>{t('live.section_title')}</span>
          <span className="bsv2__pill-status">
            <PulseDot/> {t('live.updated')}
          </span>
        </div>
        <h2 className="bsv2__hero-title bsv2__hero-title--md">
          {t('live.step_progress_x_of_y', { current: stepIdx, total: 4 })}
          <br/><span className="bsv2__accent-num">{stepName}</span>
        </h2>
        <ProgressDots stepIdx={stepIdx - 1}/>
      </div>

      <div className="bsv2__teaser bsv2__teaser--live bsv3__teaser">
        <div className="bsv2__teaser-head">
          <span>{t('live.recommended_budget')}</span>
          {hasInput && tshirt && (
            <span className="bsv2__teaser-avatar bsv3__teaser-avatar" aria-hidden="true">{tshirt}</span>
          )}
        </div>
        <div className="bsv2__teaser-big bsv2__teaser-big--live">
          <span className="bsv2__chf bsv3__chf">CHF</span>
          <span className="bsv2__range bsv3__range">{hasInput ? fmtChfTight(r.recommendedBudget) : '—'}</span>
        </div>
        <div className="bsv2__band bsv3__band">
          <div className="bsv2__band-track">
            <div className="bsv2__band-fill" style={{ left: '8%', right: '8%' }}></div>
            <div className="bsv2__band-pin" style={{ left: '50%' }}></div>
          </div>
          <div className="bsv2__band-labels bsv2__band-labels--triplet">
            <span>{hasInput ? fmtChfTight(r.confidenceLow) : '—'}</span>
            <span className="bsv2__band-center">{t('live.wallet_band_axis_mid')}</span>
            <span>{hasInput ? fmtChfTight(r.confidenceHigh) : '—'}</span>
          </div>
        </div>
      </div>

      <div className="bsv2__minirow">
        <div className="bsv2__mini">
          <i className="fa-regular fa-clock"></i>
          <div className="bsv2__mini-value">
            {hasInput ? t('live.tile_hours_pt_value', { hours: fmtNum(r.hours), pt: fmtNum(r.personentage) }) : '—'}
          </div>
          <div className="bsv2__mini-cap">{t('live.tile_hours_pt')}</div>
        </div>
        <div className="bsv2__mini">
          <i className="fa-solid fa-gauge-high"></i>
          <div className="bsv2__mini-value bsv2__mini-value--cx">
            <span className="bsv2__cx-letter">{hasInput ? r.cxBand : '—'}</span>
            {hasInput && (
              <span className="bsv2__cx-dots" aria-hidden="true">
                {[1,2,3,4,5].map(i => <span key={i} className={i <= r.cxDots ? 'is-on' : ''}></span>)}
              </span>
            )}
          </div>
          <div className="bsv2__mini-cap">{t('live.tile_complexity')}</div>
        </div>
      </div>

      <div className="bsv2__topdrivers">
        <div className="bsv2__topdrivers-head">
          <span>{t('live.drivers_list_head')}</span>
          <span className="bsv2__topdrivers-badge">
            {t('live.drivers_list_meta', { shown: Math.min(3, drivers.filter(d => d.weight > 0).length), total: totalDrivers })}
          </span>
        </div>
        {hasInput && drivers.some(d => d.weight > 0) ? (
          <div className="bsv2__drivers">
            {drivers.map((d, i) => (
              <div key={d.id || i} className="bsv2__driver">
                <div className="bsv2__driver-line">
                  <span><span className="bsv2__driver-rank">{i + 1}</span>{d.label}</span>
                  <span className="bsv2__driver-pct">{d.pct}%</span>
                </div>
                <div className="bsv2__driver-bar"><span style={{ width: d.pct + '%' }}></span></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bsv2__driver-empty" style={{ fontSize: 12.5, color: 'var(--text-color-soft)', padding: '6px 0' }}>
            {t('live.drivers_list_empty')}
          </div>
        )}
      </div>
    </aside>
  );
};

// =============================================================================
// SidebarResult — Step 4 (Calendly)
// =============================================================================
const CALENDLY_SRC = 'https://assets.calendly.com/assets/external/widget.js';

// Ensure widget.js is loaded exactly once. Returns a Promise that resolves
// when window.Calendly is ready.
function _loadCalendlyScript() {
  if (window.Calendly && window.Calendly.initInlineWidget) return Promise.resolve();
  if (window.__calendlyLoading) return window.__calendlyLoading;
  window.__calendlyLoading = new Promise((resolve, reject) => {
    let s = document.querySelector(`script[src="${CALENDLY_SRC}"]`);
    if (!s) {
      s = document.createElement('script');
      s.src = CALENDLY_SRC;
      s.async = true;
      document.head.appendChild(s);
    }
    s.addEventListener('load', () => resolve(), { once: true });
    s.addEventListener('error', () => reject(new Error('calendly widget.js failed to load')), { once: true });
  });
  return window.__calendlyLoading;
}

const CalendlyEmbed = ({ url }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!url || !ref.current) return;
    let cancelled = false;
    const el = ref.current;
    _loadCalendlyScript().then(() => {
      if (cancelled || !ref.current) return;
      // Calendly drops an iframe inside the container. Wipe any leftover from
      // a previous mount before re-initialising, otherwise the new init is
      // skipped and the widget stays blank on back/forward navigation.
      el.innerHTML = '';
      window.Calendly.initInlineWidget({ url, parentElement: el });
    }).catch(() => {/* network/script error: silently leave empty */});
    return () => { cancelled = true; };
  }, [url]);
  return (
    <div
      ref={ref}
      className="calendly-inline-widget"
      data-url={url}
      style={{ minWidth: 280, height: 640, width: '100%' }}
    />
  );
};

const SidebarResult = ({ state }) => {
  const base = calendlyBaseUrl();
  const code = state ? (state.configCode || generateConfigCode(state)) : null;
  const params = new URLSearchParams({
    hide_event_type_details: '1',
    hide_gdpr_banner: '1',
    primary_color: '030c26',
  });
  if (code) params.set('a2', code);
  const calUrl = `${base}?${params.toString()}`;

  const checks = [1, 2, 3].map(i => ({
    title: t(`live.discovery_perk_${i}_strong`),
    body:  t(`live.discovery_perk_${i}_body`),
  }));

  return (
    <aside className="bsv2">
      <div className="bsv2__hero bsv2__hero--compact">
        <div className="bsv2__eyebrow bsv2__eyebrow--row">
          <span>{t('live.minibar_label_next')}</span>
          <span className="bsv2__pill-status">
            <PulseDot/> {t('live.minibar_status_ready')}
          </span>
        </div>
        <h2 className="bsv2__hero-title bsv2__hero-title--md">
          {t('live.step_progress_x_of_y', { current: 4, total: 4 })}
          <br/><span className="bsv2__accent-num">{t('stepper.4')}</span>
        </h2>
        <ProgressDots stepIdx={3}/>
      </div>

      <div className="bsv2__hero bsv2__hero--compact bsv2__hero--noborder">
        <h2 className="bsv2__hero-title bsv2__hero-title--md">{t('live.discovery_title')}</h2>
        <p className="bsv2__hero-sub">{t('live.discovery_lead')}</p>
      </div>

      <ul className="bsv2__checks">
        {checks.map((c, i) => (
          <li key={i} className="bsv2__check">
            <span className="bsv2__check-mark" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="14" height="14">
                <path d="M3.5 8.5 L7 12 L13 5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <div>
              <strong>{c.title}</strong>
              <span>{c.body}</span>
            </div>
          </li>
        ))}
      </ul>

      <div className="bsv2__cal-wrap">
        <CalendlyEmbed url={calUrl}/>
      </div>
    </aside>
  );
};

// =============================================================================
// LiveEval — orchestrator (routes by step)
// =============================================================================
const LiveEval = ({ state, calc, step, onLoadCode }) => {
  const [open, setOpen] = useBodySideOpen();
  const r = calc(state);
  const hasInput = hasMeaningfulInput(state);
  const isResult = step === 'result';
  const isPre    = step === 'pre';

  let body;
  if (isPre)         body = <SidebarBriefing onLoadCode={onLoadCode}/>;
  else if (isResult) body = <SidebarResult state={state}/>;
  else               body = <SidebarLive r={r} hasInput={hasInput} step={step}/>;

  return (
    <div className="live">
      <LiveMiniBar r={r} hasInput={hasInput} open={open} onToggle={() => setOpen(!open)} isResult={isResult}/>
      <div className="live__content" id="live-sheet">{body}</div>
    </div>
  );
};

export { LiveEval };
